import { storeToRefs } from 'pinia'
import i18n from '@/lang'
import { getLangDataApi } from '@/api/common'
import { languageNodeId } from '@/config/env'
import useStore from '@/store'
import DOMPurify from 'dompurify'

/**
 * 消毒 HTML 内容，防止 XSS 攻击
 */
export const sanitizeHtml = (html: string): string => {
	if (!html || typeof html !== 'string') return ''
	return DOMPurify.sanitize(html)
}

/**
 * 根据相对路径获取图片数据
 *
 * @param url
 * @returns 图片地址
 */
export const getImgUrl = (url: string) => {
	if (!url || typeof url !== 'string') return ''
	try {
		return new URL(url, import.meta.url).href
	} catch {
		return ''
	}
}

// 获取本地图片
export const getAssetURL = (url: string) => {
	return new URL(`../assets/${url}`, import.meta.url).href
}

//表单序列化
export const serialize = (data: any) => {
	const list: any[] = []
	Object.keys(data).forEach((ele) => {
		list.push(`${encodeURIComponent(ele)}=${encodeURIComponent(data[ele])}`)
	})
	return list.join('&')
}

// 删除对象中为空数组、空字符串、null/undefined 的属性（保留 0）
export const removeEmptyProperties = (obj: any) => {
	const emptyValues = [undefined, null, '', []]
	Object.keys(obj).forEach((key) => {
		if (emptyValues.includes(obj[key])) {
			delete obj[key]
		}
	})
	return obj
}

//获取数据类型
export const getObjType = (obj: any) => {
	const toString = Object.prototype.toString
	const map = {
		'[object Boolean]': 'boolean',
		'[object Number]': 'number',
		'[object String]': 'string',
		'[object Function]': 'function',
		'[object Array]': 'array',
		'[object Date]': 'date',
		'[object RegExp]': 'regExp',
		'[object Undefined]': 'undefined',
		'[object Null]': 'null',
		'[object Object]': 'object'
	}
	if (obj instanceof Element) {
		return 'element'
	}
	return (map as any)[toString.call(obj)]
}

/**
 * 对象深拷贝
 */
export const deepClone = (data: any) => {
	const type = getObjType(data)
	let obj
	if (type === 'array') {
		obj = []
	} else if (type === 'object') {
		obj = {}
	} else {
		//不再具有下一层次
		return data
	}
	if (type === 'array') {
		for (let i = 0, len = data.length; i < len; i++) {
			obj.push!(deepClone(data[i]))
		}
	} else if (type === 'object') {
		// const Data: any = data;
		for (const key in data) {
			;(obj as any)[key] = deepClone(data[key])
		}
	}
	return obj
}

// 时间格式化dateFormatter('YYYY-MM-DD HH:mm','2021-5-19 00:00:00')
export const dateFormatter = (formatter: string, date: string | Date, isreplac = false) => {
	date = date && isreplac && typeof date === 'string' ? new Date(date.replace(/\-/g, '/')) : new Date(date)
	const Y = date.getFullYear() + '',
		M = date.getMonth() + 1,
		D = date.getDate(),
		H = date.getHours(),
		m = date.getMinutes(),
		s = date.getSeconds()
	return formatter
		.replace(/YYYY|yyyy/g, Y)
		.replace(/YY|yy/g, Y.substr(2, 2))
		.replace(/MM/g, (M < 10 ? '0' : '') + M)
		.replace(/DD/g, (D < 10 ? '0' : '') + D)
		.replace(/HH|hh/g, (H < 10 ? '0' : '') + H)
		.replace(/mm/g, (m < 10 ? '0' : '') + m)
		.replace(/ss/g, (s < 10 ? '0' : '') + s)
}

// 时间展示规则
export function caculateTimeago(dateTimeStamp: number) {
	const minute = 1000 * 60 // 把分，时，天，周，半个月，一个月用毫秒表示
	const hour = minute * 60
	const day = hour * 24
	const week = day * 7
	const now = new Date().getTime() // 获取当前时间毫秒
	const diffValue = now - dateTimeStamp // 时间差
	let result = ''

	if (diffValue < 0) {
		return ''
	}
	const minC = diffValue / minute // 计算时间差的分，时，天，周，月
	const hourC = diffValue / hour
	const dayC = diffValue / day
	const weekC = diffValue / week
	if (weekC >= 1 && weekC <= 4) {
		result = ` ${Math.floor(weekC)}周前`
	} else if (dayC >= 1 && dayC <= 6) {
		result = ` ${Math.floor(dayC)}天前`
	} else if (hourC >= 1 && hourC <= 23) {
		result = ` ${Math.floor(hourC)}小时前`
	} else if (minC >= 1 && minC <= 59) {
		result = ` ${Math.floor(minC)}分钟前`
	} else if (diffValue >= 0 && diffValue <= minute) {
		result = '刚刚'
	} else {
		const datetime = new Date()
		datetime.setTime(dateTimeStamp)
		const Nyear = datetime.getFullYear()
		const Nmonth = datetime.getMonth() + 1 < 10 ? `0${datetime.getMonth() + 1}` : datetime.getMonth() + 1
		const Ndate = datetime.getDate() < 10 ? `0${datetime.getDate()}` : datetime.getDate()
		result = `${Nyear}-${Nmonth}-${Ndate}`
	}
	return result
}

// 安全解析路径
export const resolveImagePath = (path: string) => {
	if (path.startsWith('http')) return path
	if (path.startsWith('@/')) return new URL(path.replace('@', '/src'), import.meta.url).href
	return path
}

/**
 * 动态插入css
 */

export const loadStyle = (url: string) => {
	const link = document.createElement('link')
	link.type = 'text/css'
	link.rel = 'stylesheet'
	link.href = url
	const head = document.getElementsByTagName('head')[0]
	head.appendChild(link)
}

/**
 * 下载文件流格式的Excel
 */
const MIMEType = {
	xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	pdf: 'application/pdf',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	doc: 'application/msword',
	zip: 'application/zip',
	rar: 'application/x-rar-compressed',
	'7z': 'application/x-7z-compressed'
}
export const downloadExcel = (filestream: any, fileName: any, type = 'xlsx') => {
	const exportBlob = new Blob([filestream], {
		type: MIMEType[type as keyof typeof MIMEType]
	})
	const exportUrl = URL.createObjectURL(exportBlob)
	const exportElement = document.createElement('a')
	exportElement.setAttribute('href', exportUrl)
	exportElement.setAttribute('download', fileName)
	exportElement.style.display = 'none'
	document.body.appendChild(exportElement)
	exportElement.click()
	document.body.removeChild(exportElement)
	URL.revokeObjectURL(exportUrl)
}

// 动态计算表格高度
export const computeListTableHeight = (tableRef: any) => {
	if (tableRef.value) {
		const wrapperEl = tableRef.value // 获取封装层容器元素
		const wrapperHeight = wrapperEl.parentElement.clientHeight // 获取封装层的高度
		// 遍历封装层的子元素，累加非 tableRef 元素的高度
		let occupiedHeight = 0
		Array.from(wrapperEl.parentElement.children).forEach((child: any) => {
			if (child !== wrapperEl) {
				// 排除 tableRef 元素本身
				occupiedHeight += child.getBoundingClientRect().height
			}
		})
		// 计算表格的可用高度
		const tableHeight = wrapperHeight - occupiedHeight - 48

		return tableHeight
	} else {
		return 500
	}
}

/* 生成随机len位数字 */
export const randomLenNum = (len: any, date: any) => {
	let random = ''
	random = String(Math.random()).substring(2, 2 + (len || 4))
	if (date) random = random + Date.now()
	return random
}

/**
 * 设置语言
 */

export const setLang = () => {
	const { userStore } = useStore()
	const { language } = storeToRefs(userStore)

	document.body.classList.remove('lang-' + language.value)
	document.body.classList.add('lang-' + language.value)

	return new Promise((resolve) => {
		getLangDataApi({ nodeId: languageNodeId })
			.then((res: any) => {
				if (res.code == 200) {
					const langData = res.data
					const langJson = {}
					getLangDataJson(langData, langJson, language.value)

					// 净化翻译数据，移除后端数据中可能存在的无效 @: linked format 语法
					const sanitizedLangJson = sanitizeLocaleMessages(langJson)

					//设置多语言
					const resLangJson: any = { ...sanitizedLangJson }
					try {
						i18n.global.setLocaleMessage(language.value, resLangJson)
						i18n.global.locale.value = language.value
					} catch (e) {
						console.error('setLocaleMessage failed, fallback to default locale:', e)
					}
				}
			})
			.finally(() => {
				resolve(language.value)
			})
	})
}

/**
 * 清洁翻译消息中的无效 @: linked format 语法
 * vue-i18n v11 的 @: 语法用于链接其他消息，
 * 如果数据中包含意外的 @: 开头但格式不完整会导致编译报错(Error Code 10: INVALID_LINKED_FORMAT)
 */
const sanitizeLocaleMessages = (messages: any): any => {
	if (typeof messages === 'string') {
		// 检测以 @: 或 @.modifier: 开头的值，剥离前缀
		// vue-i18n v11 会将这些解析为 linked format，无效时抛出 Error Code 10
		if (/^@:/.test(messages)) {
			return messages.replace(/^@:\s*/, '')
		}
		if (/^@\.\w+:/.test(messages)) {
			return messages.replace(/^@\.\w+:\s*/, '')
		}
		return messages
	}
	if (Array.isArray(messages)) {
		return messages.map((item) => sanitizeLocaleMessages(item))
	}
	if (messages && typeof messages === 'object') {
		const result: any = {}
		for (const key of Object.keys(messages)) {
			result[key] = sanitizeLocaleMessages(messages[key])
		}
		return result
	}
	return messages
}

//根据后端返回的数据转换多语言的json数据
const getLangDataJson = (langData: any, langJson: any, langKey: any) => {
	langData.forEach((item: any) => {
		const filedNameList: any = item.languageConfigPageReqVOList || []
		const filedNameJson: any = {}
		filedNameList.forEach((fieldItem: any) => {
			if (langKey) filedNameJson[fieldItem.filedName] = fieldItem[`${langKey}Name`]
		})
		if (item.code) {
			langJson[item.code] = filedNameJson
		} else if (filedNameList.length) {
			Object.keys(filedNameJson).forEach((key) => {
				langJson[key] = filedNameJson[key]
			})
		}
		if (item.children && item.children.length) {
			getLangDataJson(item.children, item.code ? langJson[item.code] : langJson, langKey)
		}
	})
}

/**
 * 手机号脱敏
 * @param phone 手机号字符串
 * @returns 脱敏后号码
 */
/**
 * 判断是否为移动端设备访问
 */
export const isMobileDevice = (): boolean => {
	const ua = navigator.userAgent
	return /Android|iPhone|iPad|iPod|Windows Phone|BlackBerry|Opera Mini|Mobile/i.test(ua)
}

export const maskPhone = (phone: string): string => {
	// 非字符串、空值直接原样返回
	if (!phone || typeof phone !== 'string') return phone ?? ''
	const len = phone.length
	// 长度不足等于7位，不脱敏直接返回
	if (len <= 7) return phone
	// 前3 + 中间* + 后4
	const prefix = phone.slice(0, 3)
	const suffix = phone.slice(-4)
	const star = '*'.repeat(len - 7)
	return `${prefix}${star}${suffix}`
}
